import React, { useState } from 'react';
import {
  Card, CardBody, CardHeader, CardTitle, Form, FormGroup, Label, Input,
  Button, Alert, Progress, InputGroup, FormFeedback, Spinner
} from 'reactstrap';
import { Lock, Eye, EyeOff, Shield, CheckCircle } from 'lucide-react';

interface ChangePasswordProps {
  onChangePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

const ChangePasswordComponents: React.FC<ChangePasswordProps> = ({ onChangePassword }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const validatePassword = (password: string) => {
    const errors = [];
    if (password.length < 8) errors.push('at least 8 characters');
    if (!/[A-Z]/.test(password)) errors.push('one uppercase letter');
    if (!/[a-z]/.test(password)) errors.push('one lowercase letter');
    if (!/\d/.test(password)) errors.push('one number');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) errors.push('one special character');
    return errors;
  };

  const getPasswordStrength = (password: string) => {
    const validationErrors = validatePassword(password);
    if (password.length === 0) return { strength: 0, label: '', color: '' };
    if (validationErrors.length >= 4) return { strength: 25, label: 'Very Weak', color: 'danger' };
    if (validationErrors.length >= 3) return { strength: 50, label: 'Weak', color: 'warning' };
    if (validationErrors.length >= 1) return { strength: 75, label: 'Good', color: 'info' };
    return { strength: 100, label: 'Strong', color: 'success' };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setSuccessMessage('');
    setErrorMessage('');

    const newErrors: Record<string, string> = {};
    if (!currentPassword) newErrors.currentPassword = 'Current password is required';
    if (!newPassword) newErrors.newPassword = 'New password is required';
    else {
      const validationErrors = validatePassword(newPassword);
      if (validationErrors.length > 0) newErrors.newPassword = `Must contain ${validationErrors.join(', ')}`;
    }
    if (!confirmPassword) newErrors.confirmPassword = 'Please confirm your new password';
    else if (newPassword !== confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    if (currentPassword && newPassword && currentPassword === newPassword) newErrors.newPassword = 'New password must be different';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    try {
      await onChangePassword(currentPassword, newPassword);
      setSuccessMessage('Your password has been successfully changed.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      setErrorMessage(error.message || "Failed to update password. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const passwordStrength = getPasswordStrength(newPassword);

  return (
    <div style={{ maxWidth: '600px', margin: '40px auto', padding: '20px' }}>
      {/* <Alert color="info" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Shield size={18} /> For your security, change your password regularly.
      </Alert> */}

      <Card
        style={{
          border: "none",
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
          backdropFilter: "blur(6px)",
        }}
      >
        <CardHeader
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            background: "linear-gradient(135deg, #4f8cff, #6c63ff)",
            color: "#fff",
            padding: "16px",
          }}
        >
          <div
            style={{
              padding: "10px",
              borderRadius: "12px",
              backgroundColor: "rgba(255,255,255,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Lock size={20} color="#fff" />
          </div>
          <div>
            <CardTitle tag="h5" style={{ marginBottom: "4px", color: "#fff" }}>
              Change Password
            </CardTitle>
            <p style={{ margin: 0, fontSize: "14px", color: "rgba(255,255,255,0.85)" }}>
              Update your account password
            </p>
          </div>
        </CardHeader>

        <CardBody style={{ padding: "24px" }}>
          {successMessage && <Alert color="success">{successMessage}</Alert>}
          {errorMessage && <Alert color="danger">{errorMessage}</Alert>}

          <Form onSubmit={handleSubmit} style={{ display: "grid", gap: "20px" }}>
            <FormGroup>
              <Label for="currentPassword" className="fw-bold">Current Password</Label>
              <InputGroup>
                <Input
                  id="currentPassword"
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  invalid={!!errors.currentPassword}
                  style={{
                    borderRadius: "8px 0 0 8px",
                    boxShadow: "inset 0 1px 3px rgba(0,0,0,0.05)",
                  }}
                />
                <Button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  outline
                  style={{
                    borderRadius: "0 8px 8px 0",
                    backgroundColor: "white",
                    borderColor: "#ddd",
                    transition: "all 0.2s ease-in-out",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f8f9fa")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "white")
                  }
                >
                  {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </Button>
              </InputGroup>
              {errors.currentPassword && (
                <FormFeedback className="d-block">{errors.currentPassword}</FormFeedback>
              )}
            </FormGroup>

            <FormGroup>
              <Label for="newPassword" className="fw-bold">New Password</Label>
              <InputGroup>
                <Input
                  id="newPassword"
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  invalid={!!errors.newPassword}
                  style={{
                    borderRadius: "8px 0 0 8px",
                    boxShadow: "inset 0 1px 3px rgba(0,0,0,0.05)",
                  }}
                />
                <Button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  outline
                  style={{
                    borderRadius: "0 8px 8px 0",
                    backgroundColor: "white",
                    borderColor: "#ddd",
                    transition: "all 0.2s ease-in-out",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f8f9fa")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "white")
                  }
                >
                  {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </Button>
              </InputGroup>
              {newPassword && (
                <div style={{ marginTop: "10px" }}>
                  <Progress
                    value={passwordStrength.strength}
                    color={passwordStrength.color}
                    style={{
                      height: "8px",
                      borderRadius: "8px",
                      boxShadow: "inset 0 1px 2px rgba(0,0,0,0.1)",
                    }}
                  />
                  <small style={{ color: "#666", fontWeight: "500" }}>
                    {passwordStrength.label}
                  </small>
                </div>
              )}
              {errors.newPassword && (
                <FormFeedback className="d-block">{errors.newPassword}</FormFeedback>
              )}
            </FormGroup>

            <FormGroup>
              <Label for="confirmPassword" className="fw-bold">Confirm New Password</Label>
              <InputGroup>
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  invalid={!!errors.confirmPassword}
                  style={{
                    borderRadius: "8px 0 0 8px",
                    boxShadow: "inset 0 1px 3px rgba(0,0,0,0.05)",
                  }}
                />
                <Button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  outline
                  style={{
                    borderRadius: "0 8px 8px 0",
                    backgroundColor: "white",
                    borderColor: "#ddd",
                    transition: "all 0.2s ease-in-out",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f8f9fa")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "white")
                  }
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </Button>
              </InputGroup>
              {errors.confirmPassword && (
                <FormFeedback className="d-block">{errors.confirmPassword}</FormFeedback>
              )}
              {confirmPassword && newPassword === confirmPassword && !errors.confirmPassword && (
                <div
                  style={{
                    color: "green",
                    fontSize: "14px",
                    marginTop: "5px",
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    fontWeight: 500,
                  }}
                >
                  <CheckCircle size={14} /> Passwords match
                </div>
              )}
            </FormGroup>

            <Button
              type="submit"
              color="primary"
              size="lg"
              disabled={isLoading}
              block
              style={{
                fontWeight: 600,
                borderRadius: "8px",
                padding: "10px 0",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                transition: "all 0.2s ease-in-out",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.transform = "scale(1.02)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.transform = "scale(1)")
              }
            >
              {isLoading ? (
                <>
                  <Spinner size="sm" style={{ marginRight: "8px" }} /> Updating...
                </>
              ) : (
                <>
                  <Lock size={16} style={{ marginRight: "8px" }} /> Update Password
                </>
              )}
            </Button>
          </Form>
        </CardBody>
      </Card>

    </div>
  );
};

export default ChangePasswordComponents;
